/**
 * Organization Domain Definition (domain-def@4) — accumulating org knowledge.
 *
 * `Org` (domain-model.md §3.9) — a deduplicated, AI-enrichable organisation
 * entity whose knowledge ACCUMULATES across search runs (law G12). Every
 * discovered posting `observes` its employer; a background-research pass records
 * RAW findings; a presentation pass authors the German summary.
 *
 * Re-derivation: research and summary are SPLIT (fixes the gen-2 fusion,
 * llm-workflows §4.4). `organization.Researched` = RAW evidence + sources (+ the
 * derived address register when observable); `organization.Summarized` = the
 * presented German summary carrying `PresentationMeta`.
 *
 * Dedup primitive: `Org.id` is the NORMALIZED employer slug (legal-form-stripped).
 *
 * Stream: `Agg::career::{userId}::Organization::{orgId}` (per-user, owner-scoped).
 */

import type { DomainDefinitionJSON } from "./schema"

const organizationDomain = {
  schemaVersion: "domain-def@4",
  name: "Organization",
  version: "1.0.0",
  description:
    "Org — a deduplicated, AI-enrichable organisation entity whose knowledge accumulates across search runs (posting evidence + raw research + presented summary)",

  types: {
    /** One persisted finding about an org — typed provenance, never free-form-only. */
    OrgEvidence: {
      "+": "reject",
      /** What kind of finding: "posting" | "research" | "scrape" | "inference" | … (open string, NOT an enum). */
      kind: "NonEmptyString",
      /** Where it came from (a source name, a URL host, an oracle/model name). */
      source: "NonEmptyString",
      /** The finding itself, distilled to a claim. */
      claim: "NonEmptyString",
      "url?": "string",
      "observedAt?": "Timestamp",
    },

    /** A persisted, deduplicated, enrichable organisation entity. */
    Org: {
      "+": "reject",
      /** Stable dedup identity — the normalized employer slug (legal-form stripped). */
      id: "NonEmptyString",
      owner: "UserId",
      /** Display name — the un-normalized employer as first observed. */
      name: "NonEmptyString",
      /**
       * The company's own web domains. Declared since day one and never written —
       * the pipeline only ever computed the POSTING HOST and passed it transiently to
       * employer-research. WS-E (P-B) persists the real company domains here, which is
       * what makes logo derivation and cross-run grouping possible.
       */
      "domains?": "string[]",
      /** Link into the GLOBAL enrichment index (`Enrichment.CompanyProfile.key`). */
      "companyKey?": "string",
      /** The presented German summary body (once authored). */
      "summary?": "string",
      /** Address register derived at research time when observable. */
      "register?": "AddressRegister",
      evidence: "OrgEvidence[]",
      firstSeenAt: "Timestamp",
      lastSeenAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    /** A discovered posting observed this org — posting-derived evidence accumulated. */
    OrganizationObservedPayload: {
      "+": "reject",
      owner: "UserId",
      orgId: "NonEmptyString",
      name: "NonEmptyString",
      "domains?": "string[]",
      "companyKey?": "string",
      evidence: "OrgEvidence[]",
      observedAt: "Timestamp",
    },

    /** RAW org research bundle — typed evidence + sources (+ derived register when observable). */
    OrganizationResearchedPayload: {
      "+": "reject",
      owner: "UserId",
      orgId: "NonEmptyString",
      evidence: "OrgEvidence[]",
      "register?": "AddressRegister",
      researchedAt: "Timestamp",
    },

    /** The presented German summary (PRESENTATION pass output). Accumulative — re-summarized as evidence grows. */
    OrganizationSummarizedPayload: {
      "+": "reject",
      owner: "UserId",
      orgId: "NonEmptyString",
      summary: "PresentedText",
      summarizedAt: "Timestamp",
    },
  },

  resources: {
    organization: {
      description: "A single deduplicated organisation entity",
      type: "Org",
      urnPattern: "urn:career:org:{orgId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/organizations/{orgId}" },
      authz: { visibility: "user-owned", scopes: ["organization:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Organization::{orgId}",
    events: {
      "organization.Observed": {
        description: "A discovered posting observed this org; posting-derived evidence accumulated",
        type: "OrganizationObservedPayload",
      },
      "organization.Researched": {
        description: "A background-research pass recorded RAW org findings + sources (+ derived register)",
        type: "OrganizationResearchedPayload",
      },
      "organization.Summarized": {
        description: "A presentation pass authored the German org summary (PresentationMeta + provenance map)",
        type: "OrganizationSummarizedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "organization:read": { description: "Read the owner's organisations" },
      "organization:write": { description: "Observe, research, or summarize organisations" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Organisations are owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default organizationDomain
